import React, { KeyboardEvent, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import Alert from 'Components/Alert';
import Button from 'Components/Link/Button';
import Icon from 'Components/Icon';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import OverlayScroller from 'Components/Scroller/OverlayScroller';
import PageSidebarItem from 'Components/Page/Sidebar/PageSidebarItem';
import { icons, kinds } from 'Helpers/Props';
import headerStyles from 'Components/Page/Header/PageHeader.css';
import searchStyles from 'Components/Page/Header/SeriesSearchInput.css';
import pageStyles from 'Components/Page/Page.css';
import sidebarStyles from 'Components/Page/Sidebar/PageSidebar.css';
import { clearTdarrAccessToken, TdarrApiError, tdarrPost } from './api';
import { useTdarrAuth, useTdarrStatus } from './queries';
import { TdarrAuthInfo } from './types';
import Activity from './pages/Activity';
import ApiDocumentation from './pages/ApiDocumentation';
import Automations from './pages/Automations';
import Authentication from './pages/Authentication';
import CollectionPage from './pages/CollectionPage';
import Flows from './pages/Flows';
import Help from './pages/Help';
import Libraries from './pages/Libraries';
import LibraryDetails from './pages/LibraryDetails';
import Logs from './pages/Logs';
import Nodes from './pages/Nodes';
import Notifications from './pages/Notifications';
import Options from './pages/Options';
import Overview from './pages/Overview';
import Plugins from './pages/Plugins';
import Releases from './pages/Releases';
import ReportParser from './pages/ReportParser';
import ReportViewer from './pages/ReportViewer';
import { Account, ApiKeys, SingleSignOn, TdarrRelay, Users } from './pages/RestrictedTools';
import Search from './pages/Search';
import Stats from './pages/Stats';
import Settings from './pages/Settings';
import Variables from './pages/Variables';
import { Backups, DuplicateFinder, ReportSearcher, SizeExplorer } from './pages/Tools';
import { settingsSections } from './settings';

interface NavItem {
  title: string;
  to: string;
  icon: typeof icons.ACTIVITY;
  children?: Array<{ title: string; to: string; requiresAuth?: boolean }>;
}

const navItems: NavItem[] = [
  { title: 'Tdarr', to: '/', icon: icons.OVERVIEW },
  { title: 'Nodes', to: '/nodes', icon: icons.COMPUTER },
  {
    title: 'Activity', to: '/activity/staging', icon: icons.ACTIVITY,
    children: [
      { title: 'Staging Section', to: '/activity/staging' }, { title: 'On Hold', to: '/activity/hold' },
      { title: 'Transcode Queue', to: '/activity/transcode-queue' }, { title: 'Transcode Success', to: '/activity/transcode-success' },
      { title: 'Transcode Errors', to: '/activity/transcode-error' }, { title: 'Health Queue', to: '/activity/health-queue' },
      { title: 'Health Healthy', to: '/activity/health-success' }, { title: 'Health Errors', to: '/activity/health-error' },
      { title: 'Job History', to: '/activity/history' }
    ]
  },
  { title: 'Stats', to: '/stats', icon: icons.OVERVIEW },
  { title: 'Libraries', to: '/libraries', icon: icons.FOLDER_OPEN },
  { title: 'Flows', to: '/flows', icon: icons.ORGANIZE },
  { title: 'Classic Plugins', to: '/plugins', icon: icons.SETTINGS },
  { title: 'Automations', to: '/automations', icon: icons.SCHEDULED },
  {
    title: 'Tools', to: '/tools/variables', icon: icons.EDIT,
    children: [
      { title: 'Global Variables', to: '/tools/variables' }, { title: 'Notifications', to: '/tools/notifications' },
      { title: 'Duplicate Finder', to: '/tools/duplicates' }, { title: 'Size Explorer', to: '/tools/size-explorer' },
      { title: 'Report Searcher', to: '/tools/report-searcher' }, { title: 'Report Parser', to: '/tools/report-parser' },
      { title: 'Tdarr Relay', to: '/tools/tdarr-relay' }, { title: 'API Keys', to: '/tools/api-keys' }, { title: 'API Documentation', to: '/tools/api' },
      { title: 'Backups', to: '/tools/backups' }, { title: 'Account', to: '/tools/account', requiresAuth: true }, { title: 'Users', to: '/tools/users' }, { title: 'Single Sign-On', to: '/tools/sso' }
    ]
  },
  {
    title: 'Settings', to: '/settings', icon: icons.SETTINGS,
    children: settingsSections.map((section) => ({ title: section.title, to: section.to }))
  },
  { title: 'Logs', to: '/logs', icon: icons.FILE },
  { title: 'Help', to: '/help', icon: icons.INFO },
  { title: 'Releases', to: '/releases', icon: icons.UPDATE }
];

function getParent(pathname: string) {
  if (pathname.startsWith('/activity/')) return '/activity/staging';
  if (pathname.startsWith('/tools/')) return '/tools/variables';
  if (pathname.startsWith('/settings/')) return '/settings';
  return navItems.find((item) => item.to === '/' ? pathname === '/' : pathname.startsWith(item.to))?.to ?? '/';
}

function Header({ auth, connected, onLogout, onToggle }: { auth?: TdarrAuthInfo; connected: boolean; onLogout: () => void; onToggle: () => void }) {
  const history = useHistory();
  const location = useLocation();
  const routeQuery = location.pathname === '/search' ? new URLSearchParams(location.search).get('q') ?? '' : '';
  const [search, setSearch] = useState(routeQuery);
  useEffect(() => { if (location.pathname === '/search') setSearch(routeQuery); }, [location.pathname, routeQuery]);
  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') { setSearch(''); return; }
    if (event.key === 'Enter' && search.trim()) {
      history.push(`/search?q=${encodeURIComponent(search.trim())}`);
      event.currentTarget.blur();
    }
  };
  return <div className={headerStyles.header}>
    <div className={headerStyles.logoContainer}><a className={`${headerStyles.logoLink} tdarr-brand`} href="/" aria-label="Tdarr overview"><img className={headerStyles.logo} src="/Content/Images/tdarr-logo.png" alt="Tdarr" /><span>Tdarr</span></a></div>
    <div className={headerStyles.sidebarToggleContainer}><Button id="sidebar-toggle-button" onPress={onToggle}><Icon name={icons.NAVBAR_COLLAPSE} /></Button></div>
    <div className={searchStyles.wrapper}><Icon name={icons.SEARCH} size={14} /><input className={searchStyles.input} value={search} placeholder="Search Tdarr" aria-label="Search Tdarr" onChange={(event) => setSearch(event.target.value)} onKeyDown={onSearchKeyDown} /></div>
    <div className={headerStyles.right}>{auth?.localAuth && <Button onPress={onLogout}>Sign Out</Button>}<div className="tdarr-connection"><span className={connected ? 'tdarr-dot tdarr-dot-connected' : 'tdarr-dot'} />{connected ? 'Connected' : 'Connecting'}</div></div>
  </div>;
}

function Sidebar({ auth, visible, onNavigate }: { auth?: TdarrAuthInfo; visible: boolean; onNavigate: () => void }) {
  const location = useLocation();
  const activeParent = getParent(location.pathname);
  return <div className={`${sidebarStyles.sidebarContainer} tdarr-sidebar`} style={{ transform: visible ? 'translateX(0)' : 'translateX(-210px)' }}>
    <OverlayScroller className={sidebarStyles.sidebar} scrollDirection="vertical" autoHide={false} autoScroll={true}><div>
      {navItems.map((item) => {
        const parentActive = item.to === activeParent;
        const hasActiveChild = item.children?.some((child) => location.pathname === child.to);
        return <PageSidebarItem key={item.to} iconName={item.icon} title={item.title} to={item.to} isActive={location.pathname === item.to && !hasActiveChild} isActiveParent={parentActive} isParentItem={!!item.children} onPress={onNavigate}>
          {parentActive && item.children?.filter((child) => !child.requiresAuth || auth?.localAuth).map((child) => <PageSidebarItem key={child.to} title={child.title} to={child.to} isActive={location.pathname === child.to} isParentItem={false} onPress={onNavigate} />)}
        </PageSidebarItem>;
      })}
    </div></OverlayScroller>
  </div>;
}

function ToolPlaceholder({ title }: { title: string }) {
  return <CollectionPage title={title} collection="VariablesJSONDB" empty={`${title} is retained for the next implementation slice.`} />;
}

export default function App() {
  const queryClient = useQueryClient();
  const auth = useTdarrAuth();
  const status = useTdarrStatus();
  const [sidebarVisible, setSidebarVisible] = useState(() => window.innerWidth > 768);
  const logout = useMutation({
    mutationFn: () => tdarrPost('/auth/logout'),
    onSettled: async () => {
      clearTdarrAccessToken();
      await queryClient.invalidateQueries({ queryKey: ['tdarr'] });
    }
  });
  const authBody = auth.error instanceof TdarrApiError && auth.error.body && typeof auth.error.body === 'object' ? auth.error.body as { setupAdmin?: boolean } : {};
  const requiresAuthentication = auth.error instanceof TdarrApiError && auth.error.status === 401;
  const header = <Header auth={auth.data} connected={status.isSuccess} onLogout={() => logout.mutate()} onToggle={() => setSidebarVisible((value) => !value)} />;
  if (auth.isPending) return <div className={pageStyles.page}>{header}<div className={pageStyles.main}><PageContent title="Tdarr"><PageContentBody><Alert>Checking authentication…</Alert></PageContentBody></PageContent></div></div>;
  if (requiresAuthentication) return <div className={pageStyles.page}>{header}<div className={pageStyles.main}><Authentication setupAdmin={Boolean(authBody.setupAdmin)} /></div></div>;
  if (auth.isError) return <div className={pageStyles.page}>{header}<div className={pageStyles.main}><PageContent title="Tdarr"><PageContentBody><Alert kind={kinds.DANGER}>Tdarr authentication status could not be determined.</Alert></PageContentBody></PageContent></div></div>;
  return <div className={pageStyles.page}>
    {header}
    <div className={pageStyles.main}>
      <Sidebar auth={auth.data} visible={sidebarVisible} onNavigate={() => window.innerWidth <= 768 && setSidebarVisible(false)} />
      <Switch>
        <Route exact path="/" component={Overview} /><Route path="/nodes" component={Nodes} /><Route path="/activity/:view?" component={Activity} /><Route path="/stats" component={Stats} />
        <Route path="/libraries/:libraryId/:tab?" component={LibraryDetails} /><Route exact path="/libraries" component={Libraries} />
        <Route path="/flows/:flowId?" component={Flows} />
        <Route path="/plugins" component={Plugins} /><Route path="/automations" component={Automations} />
        <Route path="/tools/variables" component={Variables} /><Route path="/tools/notifications" component={Notifications} />
        <Route path="/tools/duplicates" component={DuplicateFinder} /><Route path="/tools/size-explorer" component={SizeExplorer} />
        <Route path="/tools/report-searcher" component={ReportSearcher} /><Route path="/tools/report-parser" component={ReportParser} /><Route path="/tools/api-keys" component={ApiKeys} /><Route path="/tools/api" component={ApiDocumentation} /><Route path="/tools/backups" component={Backups} />
        <Route path="/tools/tdarr-relay" component={TdarrRelay} /><Route path="/tools/account" component={Account} /><Route path="/tools/users" component={Users} /><Route path="/tools/sso" component={SingleSignOn} />
        <Route path="/tools/:tool" render={({ match }) => <ToolPlaceholder title={String(match.params.tool).split('-').map((word: string) => word[0].toUpperCase() + word.slice(1)).join(' ')} />} />
        <Route path="/search" component={Search} /><Route path="/view-reports/:jobFileId" component={ReportViewer} />
        <Route exact path="/settings" component={Settings} /><Route exact path="/settings/notifications" component={Notifications} /><Route path="/settings/:category" component={Options} /><Redirect exact from="/options" to="/settings" />
        <Route path="/logs" component={Logs} /><Route path="/help" component={Help} /><Route path="/releases" component={Releases} />
        <Redirect to="/" />
      </Switch>
    </div>
  </div>;
}
